import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { Alert, Heading, Icon, Icons, Link, LinkButton, Loading, Text } from "@phoenix/components";
import { useNotifyError } from "@phoenix/contexts";

import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

interface TemplateCategorySummary {
  category: string;
  count: number;
}

const pageContainer = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-100);
  padding: var(--global-dimension-size-100);
  width: 100%;
  height: calc(100vh - 64px);
  overflow-y: auto;
  box-sizing: border-box;
`;

const heroGrid = css`
  display: grid;
  gap: var(--global-dimension-size-100);
`;

const breadcrumbRow = css`
  display: flex;
  gap: var(--global-dimension-size-75);
  align-items: center;
  flex-wrap: wrap;
`;

const breadcrumbLink = css`
  font-size: 0.875rem;
  color: var(--global-text-color-500);
  text-decoration: none;
  &:hover {
    color: var(--global-text-color-900);
    text-decoration: underline;
  }
`;

const categoryGrid = css`
  display: grid;
  gap: var(--global-dimension-size-100);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const categoryCard = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 220px;
  border-radius: 28px;
  border: 1px solid var(--global-border-color-default);
  background: var(--global-color-gray-75);
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.06);
  padding: var(--global-dimension-size-100);
  transition: transform 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 24px 80px rgba(15, 23, 42, 0.12);
  }
`;

const iconBadge = css`
  width: 48px;
  height: 48px;
  display: grid;
  align-items: center;
  justify-items: center;
  border-radius: 16px;
  background: rgba(59, 130, 246, 0.12);
`;

const categoryHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--global-dimension-size-75);
`;

const badgePill = css`
  display: inline-flex;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: var(--global-text-color-900);
  font-weight: 600;
  font-size: 0.875rem;
`;

const cardFooter = css`
  margin-top: auto;
`;

const categoryDescription = css`
  margin-top: var(--global-dimension-size-50);
  color: var(--global-text-color-500, #64748b) !important;
  font-size: 13px !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
`;

const categoryTitle = (category: string) => {
  if (category === "sql_evaluation") {
    return "SQL Evaluation";
  }
  if (category === "rag" || category === "rag_evaluation") {
    return "RAG Evaluation";
  }
  if (category === "behavior_evaluation") {
    return "Behavior Evaluations";
  }
  if (category === "output_format") {
    return "Output Format Evaluations";
  }
  if (category === "code_generation") {
    return "Code Generation Evaluations";
  }
  if (category === "model_graded_metrics") {
    return "Model-graded metrics";
  }
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const categoryDescriptionText = (category: string) => {
  return `Explore ${categoryTitle(category)} evaluators and run a matching Evalight template against your traced data.`;
};

const categoryIcon = (category: string) => {
  switch (category) {
    case "tool_call":
      return <Icons.WrenchOutline />;
    case "rag":
    case "rag_evaluation":
      return <Icons.SearchOutline />;
    case "reasoning":
      return <Icons.Brain />;
    case "agent":
      return <Icons.Robot />;
    case "safety":
    case "model_graded_metrics":
      return <Icons.ShieldOutline />;
    case "test":
      return <Icons.CheckmarkCircleOutline />;
    case "behavior_evaluation":
      return <Icons.Brain />;
    case "output_format":
      return <Icons.TemplateOutline />;
    case "code_generation":
      return <Icons.Code />;
    case "User_Experience":
      return <Icons.Code />;
    case "Knowledge_accuracy":
      return <Icons.CheckmarkCircleOutline />;
    case "Observability":
      return <Icons.EyeOutline />;
    case "sql_evaluation":
      return <Icons.DatabaseOutline />;
    case "deterministic_metrics":
      return <Icons.CheckmarkCircleOutline />;
    case "Consistency_evaluation":
      return <Icons.RepeatOutline />;
    default:
      return <Icons.TemplateOutline />;
  }
};

export function TraceTemplatesPage() {
  const notifyError = useNotifyError();
  const [categories, setCategories] = useState<TemplateCategorySummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const response = await authFetch(prependBasename("/v1/templates"));
        if (!response.ok) {
          throw new Error(`Unable to load template categories: ${response.statusText}`);
        }
        const data = (await response.json()) as TemplateCategorySummary[];
        setCategories(data);
      } catch (error) {
        notifyError({
          title: "Failed to load template categories",
          message: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, [notifyError]);

  return (
    <div css={pageContainer}>
      <div css={breadcrumbRow}>
        <Link css={breadcrumbLink} to="/templates">
          Templates
        </Link>
        <Text color="text-400">/</Text>
        <Text color="text-500">Trace Templates</Text>
      </div>

      <div css={heroGrid}>
        <div>
          <Heading level={1}>Trace Evaluation Templates</Heading>
          <Text color="text-500" size="M" css={css`margin-top: var(--global-dimension-size-25);`}>
            Choose a category to explore evaluation templates applied to traces. Templates run evaluator scripts and write annotations to your traced data.
          </Text>
        </div>

        <Alert variant="info" banner icon={<Icon svg={<Icons.InfoFilled />} />}>
          Evaluation templates are preconfigured evaluator pipelines that run on traced data and generate annotations automatically.
        </Alert>
      </div>

      {loading ? (
        <div css={css`min-height: 260px; display: grid; place-items: center;`}>
          <Loading />
        </div>
      ) : categories.length === 0 ? (
        <Text color="text-500">No template categories are currently registered.</Text>
      ) : (
        <div css={categoryGrid}>
          {categories.map((category) => (
            <div key={category.category} css={categoryCard}>
              <div css={categoryHeader}>
                <div css={iconBadge}>
                  <Icon svg={categoryIcon(category.category)} />
                </div>
                <span css={badgePill}>{category.count} evaluators</span>
              </div>
              <div css={css`margin-top: var(--global-dimension-size-75);`}>
                <Heading level={3}>{categoryTitle(category.category)}</Heading>
                <Text css={categoryDescription}>{categoryDescriptionText(category.category)}</Text>
              </div>
              <div css={cardFooter}>
                <LinkButton to={`/templates/${category.category}`} variant="default">
                  Open Category
                </LinkButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
